import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';

import { green } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';

import Autocomplete from '@material-ui/lab/Autocomplete';

import { AuthContext } from '../../context/Auth/AuthContext';
import toastError from '../../errors/toastError';
import api from '../../services/api';
import GroupedUserSelect from '../GroupedUserSelect';

// const filter = createFilterOptions({
//   trim: true
// });

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: '100%'
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  chip: {
    margin: 2
  },
  priorityChip: {
    fontWeight: 'bold'
  }
}));

const TaskSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'Título deve ter pelo menos 3 caracteres')
    .required('Título é obrigatório'),
  contactId: Yup.number().nullable(),
  userIds: Yup.array().of(Yup.number()).min(1, 'Selecione pelo menos um atendente'),
  priority: Yup.string().oneOf(['baixa', 'media', 'alta']).required('Prioridade é obrigatória'),
  status: Yup.string().oneOf(['pendente', 'em_andamento', 'concluida', 'vencida']).required('Status é obrigatório')
});

const TaskModal = ({ open, onClose, taskId, reload, initialContactId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  // Função para verificar permissões do usuário
  const canEditAllFields = () => {
    if (!taskId) return true; // Nova tarefa - todos os campos editáveis
    
    // Verificar se o usuário está no array de userIds
    let isInUserIds = false;
    if (task.userIds) {
      try {
        const userIdsArray = typeof task.userIds === 'string' ? JSON.parse(task.userIds) : task.userIds;
        isInUserIds = userIdsArray.includes(user.id);
      } catch (e) {
        // Se houver erro ao parsear, continua sem considerar userIds
      }
    }
    
    return user.profile === 'admin' || user.super || task.createdByUserId === user.id || isInUserIds;
  };

  const canEditStatus = () => {
    if (!taskId) return true; // Nova tarefa
    return canEditAllFields() || task.userId === user.id;
  };

  const canViewTask = () => {
    if (!taskId) return true; // Nova tarefa
    return canEditAllFields() || task.userId === user.id;
  };



  const initialState = {
    title: '',
    description: '',
    contactId: null,
    userId: '',
    userIds: [],
    priority: 'media',
    status: 'pendente',
    dueDate: '',
    tagIds: []
  };

  const [task, setTask] = useState(initialState);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar contatos
        const { data: contactsData } = await api.get('/contacts', {
          params: { pageNumber: 1, searchParam: '' }
        });
        setContacts(contactsData.contacts || []);

        // Buscar usuários
        const { data: usersData } = await api.get('/users', {
          params: { showAll: true }
        });
        setUsers(usersData.users || []);

        // Buscar tags
        const { data: tagsData } = await api.get('/tags/list');
        setTags(tagsData || []);
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  // Busca de contatos com debounce
  useEffect(() => {
    if (!open || searchParam.length < 3) {
      setContactLoading(false);
      return;
    }
    setContactLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get('contacts', {
            params: { searchParam }
          });
          setContactOptions(data.contacts);
          setContactLoading(false);
        } catch (err) {
          setContactLoading(false);
          toastError(err);
        }
      };

      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, open]);

  useEffect(() => {
    const fetchTask = async () => {
      if (!taskId) {
        // Se não há taskId, resetar para o estado inicial
        setTask(initialState);
        setSelectedContact(null);
        setContactOptions([]);
        return;
      }
      
      try {
        const { data } = await api.get(`/tasks/${taskId}`);
        
        // Processar userIds
        let userIdsArray = [];
        if (data.userIds) {
          try {
            userIdsArray = typeof data.userIds === 'string' ? JSON.parse(data.userIds) : data.userIds;
          } catch (e) {
            console.error('Erro ao parsear userIds:', e);
          }
        }
        
        setTask({
          ...data,
          tagIds: data.tagIds ? JSON.parse(data.tagIds) : [],
          userIds: userIdsArray,
          dueDate: data.dueDate ? moment(data.dueDate).format('YYYY-MM-DD') : '',
          status: data.status || 'pendente'
        });
        
        // Se há um contactId, buscar o contato específico para o Autocomplete
        if (data.contactId) {
          const fetchTaskContact = async () => {
            try {
              const { data: contactData } = await api.get(`/contacts/${data.contactId}`);
              setSelectedContact(contactData);
              setContactOptions([contactData]);
            } catch (err) {
              console.error('Erro ao buscar contato da tarefa:', err);
              // Se não conseguir buscar o contato específico, tentar encontrar nos contatos já carregados
              const contact = contacts.find(c => c.id === data.contactId);
              if (contact) {
                setSelectedContact(contact);
                setContactOptions([contact]);
              }
            }
          };
          
          fetchTaskContact();
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchTask();
  }, [taskId]);

  // useEffect para lidar com contato inicial
  useEffect(() => {
    const fetchInitialContact = async () => {
      if (!initialContactId || taskId) {
        return; // Só aplica se não for edição de tarefa existente
      }
      
      try {
        const { data: contactData } = await api.get(`/contacts/${initialContactId}`);
        setSelectedContact(contactData);
        setContactOptions([contactData]);
        setTask(prev => ({ ...prev, contactId: initialContactId }));
      } catch (err) {
        console.error('Erro ao buscar contato inicial:', err);
        // Se não conseguir buscar, tentar encontrar nos contatos já carregados
        const contact = contacts.find(c => c.id === initialContactId);
        if (contact) {
          setSelectedContact(contact);
          setContactOptions([contact]);
          setTask(prev => ({ ...prev, contactId: initialContactId }));
        }
      }
    };

    if (open && initialContactId && !taskId) {
      fetchInitialContact();
    }
  }, [open, initialContactId, taskId, contacts]);

  const handleClose = () => {
    setTask(initialState);
    setSearchParam('');
    setSelectedContact(null);
    setContactOptions([]);
    setContactLoading(false);
    setStatusLoading(false);
    onClose();
  };

  const handleStatusChange = async (newStatus, setFieldValue) => {
    if (!taskId) {
      // Se não é uma tarefa existente, apenas atualizar o campo
      setFieldValue('status', newStatus);
      return;
    }

    if (!canEditStatus()) {
      toast.error('Você não tem permissão para alterar o status desta tarefa.');
      return;
    }

    try {
      setStatusLoading(true);
      
      // Fazer update apenas do status
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      
      // Atualizar o estado local
      setTask(prev => ({ ...prev, status: newStatus }));
      setFieldValue('status', newStatus);
      
      toast.success('Status atualizado com sucesso!');
      
      // Atualizar a tabela
      if (typeof reload === 'function') {
        reload();
      }
    } catch (err) {
      toastError(err);
      // Reverter para o status anterior em caso de erro
      setFieldValue('status', task.status);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSaveTask = async values => {
    try {
      setLoading(true);
      
      const taskData = {
        ...values,
        dueDate: values.dueDate ? moment(values.dueDate).endOf('day').format() : null,
        tagIds: values.tagIds,
        userIds: values.userIds || [],
        userId: values.userIds && values.userIds.length > 0 ? values.userIds[0] : null
      };

      if (taskId) {
        await api.put(`/tasks/${taskId}`, taskData);
        toast.success('Tarefa atualizada com sucesso!');
      } else {
        await api.post('/tasks', taskData);
        toast.success('Tarefa criada com sucesso!');
      }

      if (typeof reload === 'function') {
        reload();
      }
      
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta': return '#f44336';
      case 'media': return '#ff9800';
      case 'baixa': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      alta: 'Alta',
      media: 'Média',
      baixa: 'Baixa'
    };
    return labels[priority] || priority;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9800';
      case 'em_andamento': return '#2196f3';
      case 'concluida': return '#4caf50';
      case 'vencida': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pendente: 'Pendente',
      em_andamento: 'Em andamento',
      concluida: 'Concluída',
      vencida: 'Vencida'
    };
    return labels[status] || status;
  };

  const handleSelectContact = (e, newValue) => {
    if (newValue?.id) {
      setSelectedContact(newValue);
    } else {
      setSelectedContact(null);
    }
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  // Se o usuário não tem permissão para ver a tarefa, não renderizar o modal
  if (open && taskId && !canViewTask()) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {taskId 
            ? (canEditAllFields() ? 'Editar Tarefa' : 'Visualizar Tarefa') 
            : 'Nova Tarefa'
          }
        </DialogTitle>
        
        <Formik
          initialValues={task}
          enableReinitialize={true}
          validationSchema={TaskSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveTask(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Título"
                      name="title"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      disabled={!canEditAllFields()}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={contactOptions}
                      loading={contactLoading}
                      clearOnBlur
                      autoHighlight
                      freeSolo
                      clearOnEscape
                      getOptionLabel={renderOptionLabel}
                      renderOption={renderOptionLabel}
                      value={selectedContact || contactOptions.find(contact => contact.id === values.contactId) || null}
                      onChange={(e, newValue) => {
                        handleSelectContact(e, newValue);
                        setFieldValue('contactId', newValue ? newValue.id : null);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Contato (opcional)"
                          variant="outlined"
                          margin="dense"
                          error={touched.contactId && Boolean(errors.contactId)}
                          helperText={touched.contactId && errors.contactId}
                          onChange={e => setSearchParam(e.target.value)}
                          placeholder="Digite o nome ou telefone do contato..."
                          disabled={!canEditAllFields()}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {contactLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      disabled={!canEditAllFields()}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <GroupedUserSelect
                      users={users}
                      value={values.userIds}
                      onChange={(newValue) => setFieldValue('userIds', newValue)}
                      error={touched.userIds && Boolean(errors.userIds)}
                      helperText={touched.userIds && errors.userIds}
                      disabled={!canEditAllFields()}
                      required
                      label="Atendentes"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                      required
                    >
                      <InputLabel>Prioridade</InputLabel>
                      <Select
                        value={values.priority}
                        onChange={e => setFieldValue('priority', e.target.value)}
                        label="Prioridade"
                        disabled={!canEditAllFields()}
                      >
                        <MenuItem value="baixa">
                          <Chip 
                            label={getPriorityLabel('baixa')} 
                            style={{ backgroundColor: getPriorityColor('baixa'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                        <MenuItem value="media">
                          <Chip 
                            label={getPriorityLabel('media')} 
                            style={{ backgroundColor: getPriorityColor('media'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                        <MenuItem value="alta">
                          <Chip 
                            label={getPriorityLabel('alta')} 
                            style={{ backgroundColor: getPriorityColor('alta'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                      error={touched.status && Boolean(errors.status)}
                      required
                    >
                      <InputLabel>
                        Status {statusLoading && <CircularProgress size={14} style={{ marginLeft: 8 }} />}
                      </InputLabel>
                      <Select
                        value={values.status}
                        onChange={e => handleStatusChange(e.target.value, setFieldValue)}
                        label="Status"
                        disabled={statusLoading || !canEditStatus()}
                      >
                        <MenuItem value="pendente">
                          <Chip 
                            label={getStatusLabel('pendente')} 
                            style={{ backgroundColor: getStatusColor('pendente'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                        <MenuItem value="em_andamento">
                          <Chip 
                            label={getStatusLabel('em_andamento')} 
                            style={{ backgroundColor: getStatusColor('em_andamento'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                        <MenuItem value="concluida">
                          <Chip 
                            label={getStatusLabel('concluida')} 
                            style={{ backgroundColor: getStatusColor('concluida'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                        <MenuItem value="vencida">
                          <Chip 
                            label={getStatusLabel('vencida')} 
                            style={{ backgroundColor: getStatusColor('vencida'), color: 'white' }}
                            size="small"
                          />
                        </MenuItem>
                      </Select>
                      {touched.status && errors.status && (
                        <FormHelperText>{errors.status}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Data de Vencimento"
                      name="dueDate"
                      type="date"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      disabled={!canEditAllFields()}
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{
                        min: moment().format('YYYY-MM-DD') // Data mínima é hoje, sem limite máximo
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>Tags</InputLabel>
                      <Select
                        multiple
                        value={values.tagIds}
                        onChange={e => setFieldValue('tagIds', e.target.value)}
                        label="Tags"
                        disabled={!canEditAllFields()}
                        renderValue={(selected) => (
                          <div className={classes.chips}>
                            {selected.map((tagId) => {
                              const tag = tags.find(t => t.id === tagId);
                              return tag ? (
                                <Chip
                                  key={tagId}
                                  label={tag.name}
                                  className={classes.chip}
                                  style={{
                                    backgroundColor: tag.color,
                                    color: '#fff'
                                  }}
                                  size="small"
                                />
                              ) : null;
                            })}
                          </div>
                        )}
                      >
                        {tags.map(tag => (
                          <MenuItem key={tag.id} value={tag.id}>
                            <Chip
                              label={tag.name}
                              style={{
                                backgroundColor: tag.color,
                                color: '#fff'
                              }}
                              size="small"
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Descrição"
                      name="description"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      minRows={4}
                      disabled={!canEditAllFields()}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              
              <DialogActions>
                <Button onClick={handleClose} color="secondary">
                  {canEditAllFields() ? 'Cancelar' : 'Fechar'}
                </Button>
                {canEditAllFields() && (
                  <div className={classes.btnWrapper}>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={loading}
                    >
                      {taskId ? 'Salvar' : 'Criar'}
                    </Button>
                    {loading && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TaskModal;